import { useState, useCallback, useRef, useEffect, type FormEvent, type ChangeEvent } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiFetch } from "../../utils/api";
import { AppHeader } from "../../components/AppHeader";
import { AppFooter } from "../../components/AppFooter";
import { formatPrice, formatArea } from "../../utils/format";
import { useKakaoMap } from "./useKakaoMap";
import { useIsMobile } from "../../hooks/useIsMobile";
import { usePageTitle } from "../../hooks/usePageTitle";
import {
  PROPERTY_TYPES,
  type ListingForm,
  type PropertyType,
} from "./types";

const LAND_CATEGORIES = [
  "전", "답", "임야", "대", "잡종지", "과수원", "공장용지", "창고용지", "기타",
];
const ZONING_OPTIONS = [
  "계획관리", "보전관리", "생산관리", "자연녹지", "농림지역", "기타",
];
const MAX_PHOTOS = 10;

function resizeImage(file: File, maxSize = 800, quality = 0.7): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let w = img.width;
      let h = img.height;
      if (w > maxSize || h > maxSize) {
        if (w > h) { h = Math.round((h * maxSize) / w); w = maxSize; }
        else { w = Math.round((w * maxSize) / h); h = maxSize; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) { reject(new Error("Canvas not supported")); return; }
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Image load failed")); };
    img.src = url;
  });
}

export function EditListingPage() {
  usePageTitle("\uB9E4\uBB3C \uC218\uC815");
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [loadingData, setLoadingData] = useState(true);
  const [form, setForm] = useState<ListingForm>({
    property_type: "",
    region_sido: "",
    region_sigungu: "",
    region_eupmyeondong: "",
    exact_address: "",
    area_pyeong: "",
    price_manwon: "",
    exact_lat: null,
    exact_lng: null,
    blind_lat: null,
    blind_lng: null,
    land_category: "",
    zoning: "",
    road_access: "",
    water_supply: "",
    recommended_use: "",
    description: "",
    detailed_notes: "",
    owner_contact: "",
    photos: [],
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const update = useCallback(
    <K extends keyof ListingForm>(key: K, value: ListingForm[K]) =>
      setForm((prev) => ({ ...prev, [key]: value })),
    [],
  );

  const handlePinChange = useCallback((lat: number, lng: number) => {
    setForm((prev) => ({ ...prev, exact_lat: lat, exact_lng: lng }));
  }, []);

  const { searchAddress, calcBlindCoords } = useKakaoMap({
    containerId: "edit-listing-map",
    onPinChange: handlePinChange,
    level: 4,
  });

  // 기존 매물 데이터 로드
  useEffect(() => {
    if (!id) return;
    apiFetch<any>(`/api/listings/${id}/edit`)
      .then((data) => {
        setForm({
          property_type: data.property_type || "",
          region_sido: data.region_sido || "",
          region_sigungu: data.region_sigungu || "",
          region_eupmyeondong: data.region_eupmyeondong || "",
          exact_address: data.exact_address || "",
          area_pyeong: data.area_pyeong ? String(data.area_pyeong) : "",
          price_manwon: data.price_manwon ? String(data.price_manwon) : "",
          exact_lat: data.exact_lat || null,
          exact_lng: data.exact_lng || null,
          blind_lat: data.blind_lat || null,
          blind_lng: data.blind_lng || null,
          land_category: data.land_category || "",
          zoning: data.zoning || "",
          road_access: data.road_access || "",
          water_supply: data.water_supply || "",
          recommended_use: data.recommended_use || "",
          description: data.description || "",
          detailed_notes: data.detailed_notes || "",
          owner_contact: data.owner_contact || "",
          photos: data.photos || [],
        });
      })
      .catch((err) => setError(err.message || "매물 정보를 불러올 수 없습니다"))
      .finally(() => setLoadingData(false));
  }, [id]);

  const handleCalcBlind = async () => {
    if (!form.region_sido || !form.region_sigungu) return;
    const coords = await calcBlindCoords(form.region_sido, form.region_sigungu, form.region_eupmyeondong);
    if (coords) setForm((prev) => ({ ...prev, blind_lat: coords.lat, blind_lng: coords.lng }));
  };

  const handlePhotos = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const remaining = MAX_PHOTOS - form.photos.length;
    if (remaining <= 0) return;
    const toProcess = Array.from(files).slice(0, remaining);
    const newPhotos: string[] = [];
    for (const file of toProcess) {
      try { newPhotos.push(await resizeImage(file)); } catch { /* skip */ }
    }
    if (newPhotos.length > 0) setForm((prev) => ({ ...prev, photos: [...prev.photos, ...newPhotos] }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removePhoto = (index: number) => {
    setForm((prev) => ({ ...prev, photos: prev.photos.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    if (!form.property_type) { setError("매물 유형을 선택해 주세요"); return; }
    if (!form.region_sido || !form.region_sigungu || !form.region_eupmyeondong) {
      setError("소재지(시도/시군구/읍면동)를 모두 입력해 주세요"); return;
    }
    if (!form.exact_address) { setError("정확한 지번 주소를 입력해 주세요"); return; }
    if (!form.exact_lat || !form.exact_lng) { setError("지도에서 매물 위치를 클릭해 주세요"); return; }
    const areaPyeong = parseFloat(form.area_pyeong);
    const priceManwon = parseFloat(form.price_manwon);
    if (!areaPyeong || areaPyeong <= 0) { setError("면적을 입력해 주세요"); return; }
    if (!priceManwon || priceManwon <= 0) { setError("가격을 입력해 주세요"); return; }

    let blindLat = form.blind_lat;
    let blindLng = form.blind_lng;
    if (!blindLat || !blindLng) {
      const coords = await calcBlindCoords(form.region_sido, form.region_sigungu, form.region_eupmyeondong);
      if (coords) { blindLat = coords.lat; blindLng = coords.lng; }
      else { setError("블라인드 좌표 계산에 실패했습니다"); return; }
    }

    setLoading(true);
    try {
      await apiFetch(`/api/listings/${id}`, {
        method: "PATCH",
        body: JSON.stringify({
          property_type: form.property_type,
          region_sido: form.region_sido,
          region_sigungu: form.region_sigungu,
          region_eupmyeondong: form.region_eupmyeondong,
          exact_address: form.exact_address,
          area_pyeong: areaPyeong,
          price_manwon: priceManwon,
          exact_lat: form.exact_lat,
          exact_lng: form.exact_lng,
          blind_lat: blindLat,
          blind_lng: blindLng,
          land_category: form.land_category || undefined,
          zoning: form.zoning || undefined,
          road_access: form.road_access || undefined,
          water_supply: form.water_supply || undefined,
          recommended_use: form.recommended_use || undefined,
          description: form.description || undefined,
          detailed_notes: form.detailed_notes || undefined,
          owner_contact: form.owner_contact || undefined,
          photos: form.photos,
        }),
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || "매물 수정에 실패했습니다");
    } finally {
      setLoading(false);
    }
  };

  const inputFontSize = 16;

  if (success) {
    return (
      <div style={styles.page}>
        <AppHeader backTo="/mypage" backLabel="\u2190 \uB9C8\uC774\uD398\uC774\uC9C0" />
        <div style={{ ...styles.container, padding: isMobile ? "0 16px 48px" : "0 32px 48px" }}>
          <div style={styles.successCard}>
            <p style={styles.successIcon}>{"✓"}</p>
            <h2 style={styles.successTitle}>{"\uB9E4\uBB3C\uC774 \uC218\uC815\uB418\uC5C8\uC2B5\uB2C8\uB2E4"}</h2>
            <p style={styles.successDesc}>{"\uBCC0\uACBD\uC0AC\uD56D\uC774 \uC800\uC7A5\uB418\uC5C8\uC2B5\uB2C8\uB2E4"}</p>
            <button
              onClick={() => navigate("/mypage")}
              style={styles.submitBtn}
            >
              {"\uB9C8\uC774\uD398\uC774\uC9C0\uB85C"}
            </button>
          </div>
        </div>
        <AppFooter />
      </div>
    );
  }

  if (loadingData) {
    return (
      <div style={styles.page}>
        <AppHeader backTo="/mypage" backLabel="\u2190 \uB9C8\uC774\uD398\uC774\uC9C0" />
        <div style={{ display: "flex", justifyContent: "center", padding: 60, color: "var(--text-muted)" }}>
          {"\uB9E4\uBB3C \uC815\uBCF4 \uBD88\uB7EC\uC624\uB294 \uC911..."}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <AppHeader backTo="/mypage" backLabel="\u2190 \uB9C8\uC774\uD398\uC774\uC9C0" />
      <div style={{ ...styles.container, padding: isMobile ? "0 16px 48px" : "0 32px 48px" }}>

        <div style={styles.pageHeader}>
          <h1 style={styles.pageTitle}>{"\uB9E4\uBB3C \uC218\uC815"}</h1>
          <p style={styles.pageDesc}>{"\uB9E4\uBB3C \uC815\uBCF4\uB97C \uC218\uC815\uD569\uB2C8\uB2E4"}</p>
        </div>

        {error && !loadingData && (
          <p style={styles.error}>{error}</p>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          {/* Section 1: 매물 유형 */}
          <Section title="\uB9E4\uBB3C \uC720\uD615">
            <SelectField
              label="\uC720\uD615 \uC120\uD0DD"
              value={form.property_type}
              onChange={(v) => update("property_type", v as PropertyType)}
              options={PROPERTY_TYPES.map((t) => ({ value: t, label: t }))}
              placeholder="\uC120\uD0DD\uD574 \uC8FC\uC138\uC694"
              required
              fontSize={inputFontSize}
            />
          </Section>

          {/* Section 2: 소재지 */}
          <Section title="\uC18C\uC7AC\uC9C0">
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
              <Field label="\uC2DC/\uB3C4" value={form.region_sido} onChange={(v) => update("region_sido", v)} placeholder="\uC608: \uACBD\uAE30\uB3C4" required fontSize={inputFontSize} />
              <Field label="\uC2DC/\uAD70/\uAD6C" value={form.region_sigungu} onChange={(v) => update("region_sigungu", v)} placeholder="\uC608: \uC6A9\uC778\uC2DC \uCC98\uC778\uAD6C" required fontSize={inputFontSize} />
            </div>
            <div style={fieldStyles.row}>
              <div style={{ flex: 1 }}>
                <Field label="\uC74D/\uBA74/\uB3D9" value={form.region_eupmyeondong} onChange={(v) => update("region_eupmyeondong", v)} placeholder="\uC608: \uD3EC\uACE1\uC74D" required fontSize={inputFontSize} />
              </div>
              <button type="button" onClick={handleCalcBlind} style={styles.blindCalcBtn} title="\uBE14\uB77C\uC778\uB4DC \uC88C\uD45C \uACC4\uC0B0">
                {"\uBE14\uB77C\uC778\uB4DC \uC88C\uD45C \uACC4\uC0B0"}
              </button>
            </div>
            {form.blind_lat && form.blind_lng && (
              <p style={styles.blindCoordText}>{"\uBE14\uB77C\uC778\uB4DC \uC88C\uD45C: "}{form.blind_lat}, {form.blind_lng}</p>
            )}
            <Field label="\uC815\uD655\uD55C \uC9C0\uBC88 \uC8FC\uC18C" value={form.exact_address} onChange={(v) => update("exact_address", v)} placeholder="\uC608: \uACBD\uAE30\uB3C4 \uC6A9\uC778\uC2DC 123" required fontSize={inputFontSize} />
          </Section>

          {/* Section 3: 위치 지도 */}
          <Section title="\uC704\uCE58 \uC9C0\uB3C4">
            <div style={fieldStyles.row}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchAddress(searchQuery); } }}
                placeholder="\uC8FC\uC18C \uB610\uB294 \uC7A5\uC18C \uAC80\uC0C9"
                style={{ ...fieldStyles.input, flex: 1, fontSize: inputFontSize }}
              />
              <button type="button" onClick={() => searchAddress(searchQuery)} style={styles.searchBtn}>{"\uAC80\uC0C9"}</button>
            </div>
            <div id="edit-listing-map" style={styles.mapContainer} />
            {form.exact_lat && form.exact_lng ? (
              <p style={styles.coordText}>{"\uD540 \uC88C\uD45C: "}{form.exact_lat.toFixed(6)}, {form.exact_lng.toFixed(6)}</p>
            ) : (
              <p style={styles.coordHint}>{"\uC9C0\uB3C4\uB97C \uD074\uB9AD\uD558\uC5EC \uC815\uD655\uD55C \uB9E4\uBB3C \uC704\uCE58\uB97C \uC9C0\uC815\uD574 \uC8FC\uC138\uC694"}</p>
            )}
          </Section>

          {/* Section 4: 매물 정보 */}
          <Section title="\uB9E4\uBB3C \uC815\uBCF4">
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
              <div>
                <Field label="\uBA74\uC801 (\uD3C9)" value={form.area_pyeong} onChange={(v) => update("area_pyeong", v)} placeholder="\uC608: 500" type="number" required fontSize={inputFontSize} />
                {form.area_pyeong && parseFloat(form.area_pyeong) > 0 && (
                  <p style={styles.previewText}>{formatArea(parseFloat(form.area_pyeong))}</p>
                )}
              </div>
              <div>
                <Field label="\uAC00\uACA9 (\uB9CC\uC6D0)" value={form.price_manwon} onChange={(v) => update("price_manwon", v)} placeholder="\uC608: 50000 (5\uC5B5)" type="number" required fontSize={inputFontSize} />
                {form.price_manwon && parseFloat(form.price_manwon) > 0 && (
                  <p style={styles.previewText}>{formatPrice(parseFloat(form.price_manwon))}{"\uC6D0"}</p>
                )}
              </div>
            </div>
            <SelectField label="\uC9C0\uBAA9" value={form.land_category} onChange={(v) => update("land_category", v)} options={LAND_CATEGORIES.map((c) => ({ value: c, label: c }))} placeholder="\uC120\uD0DD\uD574 \uC8FC\uC138\uC694" fontSize={inputFontSize} />
          </Section>

          {/* Section 5: 기타 정보 */}
          <Section title="\uAE30\uD0C0 \uC815\uBCF4 (\uC120\uD0DD)">
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
              <SelectField label="\uC6A9\uB3C4\uC9C0\uC5ED" value={form.zoning} onChange={(v) => update("zoning", v)} options={ZONING_OPTIONS.map((z) => ({ value: z, label: z }))} placeholder="\uC120\uD0DD\uD574 \uC8FC\uC138\uC694" fontSize={inputFontSize} />
              <Field label="\uB3C4\uB85C \uC811\uD569" value={form.road_access} onChange={(v) => update("road_access", v)} placeholder="\uC608: 4m \uD3EC\uC7A5\uB3C4\uB85C \uC811\uD569" fontSize={inputFontSize} />
              <Field label="\uC218\uB3C4" value={form.water_supply} onChange={(v) => update("water_supply", v)} placeholder="\uC608: \uC0C1\uC218\uB3C4, \uAD00\uC815(\uC9C0\uD558\uC218)" fontSize={inputFontSize} />
              <Field label="\uCD94\uCC9C \uC6A9\uB3C4" value={form.recommended_use} onChange={(v) => update("recommended_use", v)} placeholder="\uC608: \uC804\uC6D0\uC8FC\uD0DD, \uADC0\uB18D, \uCE94\uD551\uC7A5" fontSize={inputFontSize} />
            </div>
          </Section>

          {/* Section 6: 사진 */}
          <Section title={`\uC0AC\uC9C4 (${form.photos.length}/${MAX_PHOTOS}\uC7A5)`}>
            <input ref={fileInputRef} type="file" multiple accept="image/*" onChange={handlePhotos} style={{ display: "none" }} />
            <button type="button" onClick={() => fileInputRef.current?.click()} disabled={form.photos.length >= MAX_PHOTOS} style={{ ...styles.photoAddBtn, opacity: form.photos.length >= MAX_PHOTOS ? 0.4 : 1 }}>
              {"+ \uC0AC\uC9C4 \uCD94\uAC00"}
            </button>
            {form.photos.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(3, 1fr)" : "repeat(4, 1fr)", gap: 8 }}>
                {form.photos.map((src, i) => (
                  <div key={i} style={styles.thumbWrap}>
                    <img src={src} alt={`\uC0AC\uC9C4 ${i + 1}`} style={styles.thumbImg} />
                    <button type="button" onClick={() => removePhoto(i)} style={styles.thumbRemove}>X</button>
                  </div>
                ))}
              </div>
            )}
            <p style={styles.coordHint}>{`JPEG\uB85C \uBCC0\uD658, \uCD5C\uB300 800px \uB9AC\uC0AC\uC774\uC988 (\uCD5C\uB300 ${MAX_PHOTOS}\uC7A5)`}</p>
          </Section>

          {/* Section 7: 메모 */}
          <Section title="\uBA54\uBAA8">
            <TextareaField label="\uACF5\uAC1C \uBA54\uBAA8" value={form.description} onChange={(v) => update("description", v)} placeholder="\uB2E4\uB978 \uC911\uAC1C\uC0AC\uC5D0\uAC8C \uBCF4\uC774\uB294 \uBA54\uBAA8" rows={3} fontSize={inputFontSize} />
            <TextareaField label="\uBE44\uACF5\uAC1C \uBA54\uBAA8 (NDA \uD6C4 \uACF5\uAC1C)" value={form.detailed_notes} onChange={(v) => update("detailed_notes", v)} placeholder="NDA \uCCB4\uACB0 \uD6C4\uC5D0\uB9CC \uACF5\uAC1C\uB418\uB294 \uC0C1\uC138 \uC815\uBCF4" rows={3} fontSize={inputFontSize} />
            <Field label="\uC18C\uC720\uC8FC \uC5F0\uB77D\uCC98 (NDA \uD6C4 \uACF5\uAC1C)" value={form.owner_contact} onChange={(v) => update("owner_contact", v)} placeholder="010-0000-0000" fontSize={inputFontSize} />
          </Section>

          {error && <p style={styles.error}>{error}</p>}

          <button type="submit" disabled={loading} style={{ ...styles.submitBtn, opacity: loading ? 0.6 : 1 }}>
            {loading ? "\uC218\uC815 \uC911..." : "\uB9E4\uBB3C \uC218\uC815 \uC644\uB8CC"}
          </button>
        </form>
      </div>
      <AppFooter />
    </div>
  );
}

// ── 내부 컴포넌트 ────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>{title}</h3>
      <div style={styles.sectionBody}>{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", required = false, fontSize = 14 }: {
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; type?: string; required?: boolean; fontSize?: number;
}) {
  return (
    <div style={fieldStyles.field}>
      <label style={fieldStyles.label}>{label}{required && <span style={fieldStyles.required}> *</span>}</label>
      <input type={type} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} style={{ ...fieldStyles.input, fontSize }} required={required} />
    </div>
  );
}

function SelectField({ label, value, onChange, options, placeholder, required = false, fontSize = 14 }: {
  label: string; value: string; onChange: (v: string) => void;
  options: { value: string; label: string }[]; placeholder?: string; required?: boolean; fontSize?: number;
}) {
  return (
    <div style={fieldStyles.field}>
      <label style={fieldStyles.label}>{label}{required && <span style={fieldStyles.required}> *</span>}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={{ ...fieldStyles.input, fontSize }} required={required}>
        <option value="">{placeholder || "\uC120\uD0DD"}</option>
        {options.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
    </div>
  );
}

function TextareaField({ label, value, onChange, placeholder, rows = 3, fontSize = 14 }: {
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; rows?: number; fontSize?: number;
}) {
  return (
    <div style={fieldStyles.field}>
      <label style={fieldStyles.label}>{label}</label>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={rows} style={{ ...fieldStyles.input, fontSize, resize: "vertical" as const, fontFamily: "inherit", lineHeight: 1.5 }} />
    </div>
  );
}

// ── 스타일 ──────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", background: "var(--bg-primary)" },
  container: { maxWidth: 1200, margin: "0 auto", padding: "0 16px 40px" },
  pageHeader: { padding: "28px 0 4px" },
  pageTitle: { fontSize: 22, fontWeight: 800, color: "var(--text-primary)", margin: 0, letterSpacing: "-0.3px" },
  pageDesc: { fontSize: 13, color: "var(--text-muted)", margin: "6px 0 24px" },
  form: { display: "flex", flexDirection: "column" as const, gap: 24 },
  section: {
    background: "var(--bg-card)", border: "1px solid var(--border-light)",
    borderLeft: "3px solid var(--lt-gold)", borderRadius: "var(--radius-xl)",
    overflow: "hidden", boxShadow: "var(--shadow-sm)",
  },
  sectionTitle: {
    fontSize: 13, fontWeight: 700, color: "var(--lt-gold)", margin: 0,
    padding: "12px 20px", borderBottom: "1px solid var(--border-light)",
    letterSpacing: "0.8px", textTransform: "uppercase" as const,
    background: "rgba(212,168,83,0.04)",
  },
  sectionBody: { padding: 20, display: "flex", flexDirection: "column" as const, gap: 14 },
  mapContainer: {
    width: "100%", height: 280, borderRadius: "var(--radius-lg)",
    border: "1px solid var(--border-light)", background: "var(--bg-secondary)",
  },
  searchBtn: {
    padding: "10px 16px", borderRadius: "var(--radius-md)", border: "none",
    background: "var(--lt-gold)", color: "#0F172A", fontSize: 14, fontWeight: 600,
    cursor: "pointer", whiteSpace: "nowrap" as const,
  },
  blindCalcBtn: {
    padding: "10px 12px", borderRadius: "var(--radius-md)",
    border: "1px solid var(--lt-gold)", background: "transparent",
    color: "var(--lt-gold)", fontSize: 12, fontWeight: 600,
    cursor: "pointer", whiteSpace: "nowrap" as const, alignSelf: "flex-end", marginTop: 22,
  },
  coordText: { fontSize: 12, color: "var(--lt-green)", margin: 0 },
  coordHint: { fontSize: 12, color: "var(--text-muted)", margin: 0 },
  blindCoordText: {
    fontSize: 12, color: "var(--lt-green)", margin: 0,
    padding: "4px 8px", background: "rgba(16, 185, 129, 0.1)", borderRadius: "var(--radius-sm)",
  },
  previewText: { fontSize: 13, color: "var(--lt-gold)", margin: "4px 0 0", fontWeight: 500 },
  error: {
    color: "var(--lt-red)", fontSize: 14, margin: 0,
    padding: "10px 16px", background: "rgba(239, 68, 68, 0.1)", borderRadius: "var(--radius-md)",
  },
  submitBtn: {
    padding: "16px", borderRadius: "var(--radius-md)", border: "none",
    background: "linear-gradient(135deg, #B8972E, #D4AF37)", color: "#0F172A",
    fontSize: 16, fontWeight: 700, cursor: "pointer",
    transition: "var(--transition-fast)", letterSpacing: "0.3px",
  },
  photoAddBtn: {
    padding: "12px", borderRadius: "var(--radius-md)",
    border: "2px dashed var(--border-medium)", background: "transparent",
    color: "var(--text-secondary)", fontSize: 14, fontWeight: 600, cursor: "pointer", width: "100%",
  },
  thumbWrap: {
    position: "relative" as const, aspectRatio: "1",
    borderRadius: "var(--radius-md)", overflow: "hidden", border: "1px solid var(--border-light)",
  },
  thumbImg: { width: "100%", height: "100%", objectFit: "cover" as const, display: "block" },
  thumbRemove: {
    position: "absolute" as const, top: 4, right: 4, width: 24, height: 24,
    borderRadius: "50%", border: "none", background: "rgba(0,0,0,0.6)",
    color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", padding: 0, lineHeight: 1,
  },
  successCard: {
    textAlign: "center" as const, padding: "60px 20px",
    background: "var(--bg-card)", border: "1px solid var(--border-light)",
    borderTop: "2px solid var(--lt-gold)", borderRadius: "var(--radius-xl)",
    marginTop: 40, boxShadow: "var(--shadow-md)",
  },
  successIcon: {
    fontSize: 48, color: "var(--lt-green)",
    background: "rgba(16, 185, 129, 0.15)", width: 72, height: 72,
    borderRadius: "50%", display: "flex", alignItems: "center",
    justifyContent: "center", margin: "0 auto 16px", fontWeight: 700,
  },
  successTitle: { fontSize: 22, fontWeight: 800, color: "var(--text-primary)", margin: "0 0 8px" },
  successDesc: { fontSize: 14, color: "var(--text-muted)", margin: "0 0 24px" },
};

const fieldStyles: Record<string, React.CSSProperties> = {
  field: { display: "flex", flexDirection: "column" as const, gap: 6 },
  label: { fontSize: 14, fontWeight: 500, color: "var(--text-secondary)" },
  required: { color: "var(--lt-red)" },
  input: {
    padding: "12px 14px", borderRadius: "var(--radius-md)",
    border: "1px solid var(--border-medium)", background: "var(--bg-secondary)",
    color: "var(--text-primary)", fontSize: 16, outline: "none",
    transition: "var(--transition-fast)", width: "100%", boxSizing: "border-box" as const,
  },
  row: { display: "flex", gap: 10, alignItems: "flex-start" },
};
